import { useState, useRef, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface UseVoicePreviewReturn {
  previewingVoice: string | null;
  loadingVoice: string | null;
  playVoicePreview: (voiceId: string) => Promise<void>;
  stopVoicePreview: () => void;
}

// Module-level cache for voice preview audio blobs - persists across component re-renders
// Key: voice_id, Value: { blob, blobUrl, timestamp }
const voiceCache = new Map<string, { blob: Blob; blobUrl: string; timestamp: number }>();
const CACHE_TTL_MS = 60 * 60 * 1000; // 60 minutes (voice previews don't change often)

/**
 * Get cached voice audio or return null if not cached/expired
 */
const getCachedVoice = (voiceId: string): { blob: Blob; blobUrl: string } | null => {
  const cached = voiceCache.get(voiceId);
  if (!cached) return null;

  // Check if cache has expired
  if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
    // Clean up expired entry
    URL.revokeObjectURL(cached.blobUrl);
    voiceCache.delete(voiceId);
    return null;
  }

  return { blob: cached.blob, blobUrl: cached.blobUrl };
};

/**
 * Cache a voice audio blob
 */
const cacheVoice = (voiceId: string, blob: Blob): string => {
  // Clean up old entry if exists
  const existing = voiceCache.get(voiceId);
  if (existing) {
    URL.revokeObjectURL(existing.blobUrl);
  }

  const blobUrl = URL.createObjectURL(blob);
  voiceCache.set(voiceId, { blob, blobUrl, timestamp: Date.now() });
  return blobUrl;
};

/**
 * Hook for voice preview functionality with browser-side caching
 * Handles fetching, playing, and cleanup of voice previews
 * Cache persists for 60 minutes to reduce server calls
 */
export const useVoicePreview = (): UseVoicePreviewReturn => {
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [loadingVoice, setLoadingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { toast } = useToast();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const stopVoicePreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPreviewingVoice(null);
  }, []);

  const playVoicePreview = useCallback(async (voiceId: string) => {
    // If this voice is already playing, stop it
    if (previewingVoice === voiceId) {
      stopVoicePreview();
      return;
    }

    // Stop any currently playing audio
    stopVoicePreview();

    setLoadingVoice(voiceId);

    try {
      // Check browser cache first
      const cached = getCachedVoice(voiceId);
      let blobUrl: string;

      if (cached) {
        console.log(`🎤 Voice cache HIT for: ${voiceId}`);
        blobUrl = cached.blobUrl;
      } else {
        console.log(`🎤 Voice cache MISS, fetching: ${voiceId}`);

        // Call backend API to get voice preview
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
        const response = await fetch(`${backendUrl}/voices/preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ voice_id: voiceId }),
        });

        if (!response.ok) {
          if (response.status === 429) {
            throw new Error('Too many preview requests. Please wait a moment.');
          } else if (response.status === 503) {
            throw new Error('Voice preview unavailable. Please try again later.');
          }
          throw new Error('Failed to load voice preview');
        }

        const audioBlob = await response.blob();
        console.log(`🎤 Received voice blob: ${audioBlob.size} bytes`);

        if (audioBlob.size === 0) {
          throw new Error('Received empty audio file');
        }

        // Cache the voice audio
        blobUrl = cacheVoice(voiceId, audioBlob);
      }

      // Create audio element
      const audio = new Audio(blobUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPreviewingVoice(null);
        audioRef.current = null;
        // Note: Don't revoke URL - it's managed by cache
      };

      audio.onerror = () => {
        setPreviewingVoice(null);
        audioRef.current = null;
        toast({
          title: 'Playback Error',
          description: 'Failed to play voice preview',
          variant: 'destructive',
        });
      };

      await audio.play();
      setPreviewingVoice(voiceId);
      console.log(`🎤 Voice preview playing: ${voiceId}`);

    } catch (error) {
      console.error('🎤 Voice preview error:', error);
      toast({
        title: 'Preview Failed',
        description: error instanceof Error ? error.message : 'Unable to preview voice',
        variant: 'destructive',
      });
      setPreviewingVoice(null);
    } finally {
      setLoadingVoice(null);
    }
  }, [previewingVoice, stopVoicePreview, toast]);

  return {
    previewingVoice,
    loadingVoice,
    playVoicePreview,
    stopVoicePreview,
  };
};

/**
 * Get the current cache size (for debugging)
 */
export const getVoiceCacheSize = (): number => voiceCache.size;

/**
 * Clear the voice cache (useful for testing)
 */
export const clearVoiceCache = (): void => {
  voiceCache.forEach((entry) => {
    URL.revokeObjectURL(entry.blobUrl);
  });
  voiceCache.clear();
};
